import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import colors from 'common/styles/colors';
import OrgSegmentedBySiteWidget from './OrgSegmentedBySiteWidget';

const WIDGET_NAME = 'org_fleet_health_by_site';

// Order conditions worst-relevant last for the tooltip readout.
const TOOLTIP_KEYS = ['good', 'slight', 'moderate', 'warning', 'critical'];

const SEGMENT_COLORS = {
  good: colors.normal,
  slight: colors.slight,
  moderate: colors.moderate,
  warning: colors.warning,
  critical: colors.critical,
  unknown: colors.unknown
};

const SEGMENT_KEYS_FIVE = ['good', 'slight', 'moderate', 'warning', 'critical', 'unknown'];
const SEGMENT_KEYS_THREE = ['good', 'warning', 'critical', 'unknown'];

const CONDITION_I18N = {
  good: 'widgetLibrary.conditions.good',
  slight: 'widgetLibrary.conditions.slight',
  moderate: 'widgetLibrary.conditions.moderate',
  warning: 'widgetLibrary.conditions.warning',
  critical: 'widgetLibrary.conditions.critical',
  unknown: 'widgetLibrary.conditions.unknown'
};

const segmentKeysForLevels = conditionLevels => (
  conditionLevels === 5 ? SEGMENT_KEYS_FIVE : SEGMENT_KEYS_THREE
);

const OrgFleetHealthBySite = (props) => {
  const { t } = useTranslation();

  // Fully-labelled breakdown on hover, for the org card and each site row alike,
  // so the terse "Critical" number is never ambiguous.
  // e.g. "6 good · 1 critical · 83% healthy".
  const segmentBreakdown = (entity) => {
    const segments = entity.segments || {};
    const parts = TOOLTIP_KEYS
      .filter(key => segments[key])
      .map(key => `${segments[key]} ${t(CONDITION_I18N[key], key)}`);
    const goodPct = entity.good_pct != null ? entity.good_pct : 0;
    const health = t('orgView.fleet.tooltipHealthy', { pct: goodPct, defaultValue: '{{pct}}% healthy' });
    return parts.length ? `${parts.join(' · ')} · ${health}` : health;
  };

  return (
    <OrgSegmentedBySiteWidget
      {...props}
      widgetName={WIDGET_NAME}
      siteLinkTo="/assets"
      emptyMessageKey="orgView.fleet.noData"
      emptyMessageDefault="No site health data"
      summaryLabelKey="orgView.fleet.orgSummary"
      summaryLabelDefault="Organisation asset health"
      getSegmentKeys={data => segmentKeysForLevels(data && data.condition_levels)}
      segmentColors={SEGMENT_COLORS}
      segmentLabelKeys={CONDITION_I18N}
      getSummaryValue={summary => `${summary.good_pct != null ? summary.good_pct : 0}%`}
      renderSummaryMeta={(tr, summary) => tr('orgView.fleet.meta', {
        machines: summary.total_machines != null ? summary.total_machines : 0,
        critical: summary.critical != null ? summary.critical : 0,
        defaultValue: '{{machines}} machines · {{critical}} critical'
      })}
      // Titled columns disambiguate the number: the bar is Health, the value is
      // the Critical count. Lead with what needs action, not the healthy count.
      columnHeaders={{
        bar: t('orgView.fleet.colHealth', 'Health'),
        value: t('orgView.fleet.colCritical', 'Critical')
      }}
      getSiteRowPrimary={(site) => {
        const segments = site.segments || {};
        return segments.critical || 0;
      }}
      getValueTone={(site) => {
        const segments = site.segments || {};
        return (segments.critical || 0) > 0 ? colors.critical : colors.greyD;
      }}
      getSiteTooltip={segmentBreakdown}
      getSummaryTooltip={segmentBreakdown}
    />
  );
};

OrgFleetHealthBySite.propTypes = {
  organisationId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  widgetsMetadata: PropTypes.object,
  title: PropTypes.string
};

OrgFleetHealthBySite.defaultProps = {
  widgetsMetadata: null,
  title: null
};

export default OrgFleetHealthBySite;
